import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Image,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  useToast
} from "@chakra-ui/react";
import { api } from "../../api";
import { useBranding } from "../../context/BrandingContext";

const uploadAsset = async (entity, file, extra = {}) => {
  if (!file) return null;
  const formData = new FormData();
  formData.append("file", file);
  Object.entries(extra).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });
  const response = await api.post(`/v2/branding/upload/${entity}`, formData, {
    headers: { "Content-Type": "multipart/form-data" }
  });
  return response.data.url;
};

const emptyCityForm = {
  id: null,
  cidade: "",
  estado: "",
  visual_mode: "google",
  titulo: "",
  subtitulo: "",
  background_image_url: "",
  home_image_url: "",
  map_center_lat: "",
  map_center_lng: "",
  map_zoom: "",
  ativa: true
};

const emptyEmpresaForm = {
  nome: "",
  slug: "",
  status: "ativo",
  email: "",
  telefone: "",
  site: "",
  endereco: "",
  bairro: "",
  cidade: "",
  estado: ""
};

const EmpresaBrandingConfig = ({ empresaId }) => {
  const toast = useToast();
  const { branding, availableCities, currentVisual, assetUrl, refreshBranding } = useBranding();
  const [empresaForm, setEmpresaForm] = useState(emptyEmpresaForm);
  const [brandingForm, setBrandingForm] = useState({
    display_name: "",
    primary_color: "#2f855a",
    secondary_color: "#0b6530",
    accent_color: "#7dd3fc",
    default_map_mode: "google",
    default_city: "",
    allow_city_switching: false,
    logo_url: "",
    login_background_url: "",
    home_background_url: ""
  });
  const [cityForm, setCityForm] = useState(emptyCityForm);
  const [logoFile, setLogoFile] = useState(null);
  const [loginBgFile, setLoginBgFile] = useState(null);
  const [homeBgFile, setHomeBgFile] = useState(null);
  const [cityBgFile, setCityBgFile] = useState(null);
  const [cityHomeFile, setCityHomeFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await api.get(`/v2/empresas/${empresaId}`);
        setEmpresaForm({
          nome: response.data.nome || "",
          slug: response.data.slug || "",
          status: response.data.status || "ativo",
          email: response.data.email || "",
          telefone: response.data.telefone || "",
          site: response.data.site || "",
          endereco: response.data.endereco || "",
          bairro: response.data.bairro || "",
          cidade: response.data.cidade || "",
          estado: response.data.estado || ""
        });
      } catch (err) {
        toast({ title: "Erro ao carregar empresa", status: "error", duration: 3000, isClosable: true });
      }
    };
    load();
  }, [empresaId, toast]);

  useEffect(() => {
    setBrandingForm((prev) => ({
      ...prev,
      display_name: branding.display_name || empresaForm.nome || "",
      primary_color: branding.primary_color || "#2f855a",
      secondary_color: branding.secondary_color || "#0b6530",
      accent_color: branding.accent_color || "#7dd3fc",
      default_map_mode: branding.default_map_mode || "google",
      default_city: branding.default_city || currentVisual?.cidade || "",
      allow_city_switching: Boolean(branding.allow_city_switching),
      logo_url: branding.logo_url || "",
      login_background_url: branding.login_background_url || "",
      home_background_url: branding.home_background_url || ""
    }));
  }, [branding, empresaForm.nome, currentVisual?.cidade]);

  const cityOptions = useMemo(() => availableCities || [], [availableCities]);

  const startEditCity = (city) => {
    setCityForm({
      id: city.id,
      cidade: city.cidade || "",
      estado: city.estado || "",
      visual_mode: city.visual_mode || "google",
      titulo: city.titulo || "",
      subtitulo: city.subtitulo || "",
      background_image_url: city.background_image_url || "",
      home_image_url: city.home_image_url || "",
      map_center_lat: city.map_center_lat || "",
      map_center_lng: city.map_center_lng || "",
      map_zoom: city.map_zoom || "",
      ativa: Number(city.ativa) === 1 || city.ativa === true
    });
  };

  const handleSaveEmpresa = async () => {
    setSaving(true);
    try {
      await api.put(`/v2/empresas/${empresaId}`, empresaForm);

      const logoUrl = logoFile ? await uploadAsset("logo", logoFile, { empresa_id: empresaId }) : brandingForm.logo_url;
      const loginBgUrl = loginBgFile
        ? await uploadAsset("login", loginBgFile, { empresa_id: empresaId })
        : brandingForm.login_background_url;
      const homeBgUrl = homeBgFile
        ? await uploadAsset("home", homeBgFile, { empresa_id: empresaId })
        : brandingForm.home_background_url;

      await api.put(`/v2/branding/empresa/${empresaId}`, {
        ...brandingForm,
        logo_url: logoUrl,
        login_background_url: loginBgUrl,
        home_background_url: homeBgUrl
      });

      setLogoFile(null);
      setLoginBgFile(null);
      setHomeBgFile(null);
      await refreshBranding();
      toast({ title: "White-label salvo", status: "success", duration: 3000, isClosable: true });
    } catch (err) {
      toast({ title: "Erro ao salvar configuracoes", status: "error", duration: 3000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCity = async () => {
    if (!cityForm.cidade) {
      toast({ title: "Informe a cidade", status: "warning", duration: 2500, isClosable: true });
      return;
    }

    setSaving(true);
    try {
      const cityBgUrl = cityBgFile
        ? await uploadAsset("city-bg", cityBgFile, { empresa_id: empresaId, cidade: cityForm.cidade })
        : undefined;
      const cityHomeUrl = cityHomeFile
        ? await uploadAsset("city-home", cityHomeFile, { empresa_id: empresaId, cidade: cityForm.cidade })
        : undefined;

      const payload = {
        ...cityForm,
        empresa_id: empresaId,
        background_image_url: cityBgUrl || cityForm.background_image_url || null,
        home_image_url: cityHomeUrl || cityForm.home_image_url || null
      };

      if (cityForm.id) {
        await api.put(`/v2/branding/cidades/${cityForm.id}`, payload);
      } else {
        await api.post("/v2/branding/cidades", payload);
      }

      setCityForm(emptyCityForm);
      setCityBgFile(null);
      setCityHomeFile(null);
      await refreshBranding();
      toast({ title: "Cidade configurada", status: "success", duration: 3000, isClosable: true });
    } catch (err) {
      toast({ title: "Erro ao salvar cidade", status: "error", duration: 3000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCity = async (id) => {
    try {
      await api.delete(`/v2/branding/cidades/${id}`);
      await refreshBranding();
      if (Number(cityForm.id) === Number(id)) {
        setCityForm(emptyCityForm);
      }
      toast({ title: "Cidade removida", status: "success", duration: 3000, isClosable: true });
    } catch (err) {
      toast({ title: "Erro ao remover cidade", status: "error", duration: 3000, isClosable: true });
    }
  };

  return (
    <Stack spacing={6}>
      <Box bg="white" borderRadius="xl" boxShadow="sm" p={5}>
        <Text fontWeight="bold" mb={4}>
          White-label da empresa
        </Text>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
          <Stack spacing={4}>
            <FormControl>
              <FormLabel>Nome da empresa</FormLabel>
              <Input value={empresaForm.nome} onChange={(e) => setEmpresaForm({ ...empresaForm, nome: e.target.value })} />
            </FormControl>
            <FormControl>
              <FormLabel>Slug</FormLabel>
              <Input value={empresaForm.slug} onChange={(e) => setEmpresaForm({ ...empresaForm, slug: e.target.value })} />
            </FormControl>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <FormControl>
                <FormLabel>Nome exibido</FormLabel>
                <Input
                  value={brandingForm.display_name}
                  onChange={(e) => setBrandingForm({ ...brandingForm, display_name: e.target.value })}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={empresaForm.status}
                  onChange={(e) => setEmpresaForm({ ...empresaForm, status: e.target.value })}
                >
                  <option value="ativo">Ativo</option>
                  <option value="suspenso">Suspenso</option>
                </Select>
              </FormControl>
            </SimpleGrid>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <FormControl>
                <FormLabel>E-mail da empresa</FormLabel>
                <Input value={empresaForm.email} onChange={(e) => setEmpresaForm({ ...empresaForm, email: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Telefone</FormLabel>
                <Input value={empresaForm.telefone} onChange={(e) => setEmpresaForm({ ...empresaForm, telefone: e.target.value })} />
              </FormControl>
            </SimpleGrid>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <FormControl>
                <FormLabel>Site</FormLabel>
                <Input value={empresaForm.site} onChange={(e) => setEmpresaForm({ ...empresaForm, site: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Bairro</FormLabel>
                <Input value={empresaForm.bairro} onChange={(e) => setEmpresaForm({ ...empresaForm, bairro: e.target.value })} />
              </FormControl>
            </SimpleGrid>
            <FormControl>
              <FormLabel>Endereço</FormLabel>
              <Input value={empresaForm.endereco} onChange={(e) => setEmpresaForm({ ...empresaForm, endereco: e.target.value })} />
            </FormControl>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <FormControl>
                <FormLabel>Cidade</FormLabel>
                <Input value={empresaForm.cidade} onChange={(e) => setEmpresaForm({ ...empresaForm, cidade: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Estado</FormLabel>
                <Input
                  value={empresaForm.estado}
                  onChange={(e) => setEmpresaForm({ ...empresaForm, estado: e.target.value.toUpperCase() })}
                  maxLength={2}
                />
              </FormControl>
            </SimpleGrid>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              <FormControl>
                <FormLabel>Cor primária</FormLabel>
                <Input type="color" value={brandingForm.primary_color} onChange={(e) => setBrandingForm({ ...brandingForm, primary_color: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Cor secundária</FormLabel>
                <Input type="color" value={brandingForm.secondary_color} onChange={(e) => setBrandingForm({ ...brandingForm, secondary_color: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Cor de destaque</FormLabel>
                <Input type="color" value={brandingForm.accent_color} onChange={(e) => setBrandingForm({ ...brandingForm, accent_color: e.target.value })} />
              </FormControl>
            </SimpleGrid>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <FormControl>
                <FormLabel>Modo padrão</FormLabel>
                <Select
                  value={brandingForm.default_map_mode}
                  onChange={(e) => setBrandingForm({ ...brandingForm, default_map_mode: e.target.value })}
                >
                  <option value="google">Google Maps</option>
                  <option value="image">Imagem com rede</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Cidade padrão</FormLabel>
                <Input
                  value={brandingForm.default_city}
                  onChange={(e) => setBrandingForm({ ...brandingForm, default_city: e.target.value })}
                  placeholder="Ex.: Cabo Verde"
                />
              </FormControl>
            </SimpleGrid>
            <HStack justify="space-between" borderRadius="lg" bg="#f5f8f5" p={3}>
              <Text fontSize="sm" fontWeight="medium">
                Permitir troca de cidade para usuários comuns
              </Text>
              <Switch
                isChecked={brandingForm.allow_city_switching}
                onChange={(e) => setBrandingForm({ ...brandingForm, allow_city_switching: e.target.checked })}
              />
            </HStack>
          </Stack>

          <Stack spacing={4}>
            <FormControl>
              <FormLabel>Logo</FormLabel>
              <Input type="file" accept="image/*" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
              {brandingForm.logo_url ? <Image mt={3} src={assetUrl(brandingForm.logo_url)} maxH="80px" objectFit="contain" /> : null}
            </FormControl>
            <FormControl>
              <FormLabel>Fundo da home</FormLabel>
              <Input type="file" accept="image/*" onChange={(e) => setHomeBgFile(e.target.files?.[0] || null)} />
              {brandingForm.home_background_url ? <Image mt={3} src={assetUrl(brandingForm.home_background_url)} borderRadius="lg" maxH="120px" objectFit="cover" /> : null}
            </FormControl>
            <FormControl>
              <FormLabel>Fundo do login</FormLabel>
              <Input type="file" accept="image/*" onChange={(e) => setLoginBgFile(e.target.files?.[0] || null)} />
              {brandingForm.login_background_url ? <Image mt={3} src={assetUrl(brandingForm.login_background_url)} borderRadius="lg" maxH="120px" objectFit="cover" /> : null}
            </FormControl>
          </Stack>
        </SimpleGrid>
        <Button mt={5} colorScheme="green" onClick={handleSaveEmpresa} isLoading={saving}>
          Salvar white-label
        </Button>
      </Box>

      <Box bg="white" borderRadius="xl" boxShadow="sm" p={5}>
        <Text fontWeight="bold" mb={4}>
          Cidades e modos visuais
        </Text>
        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
          <Stack spacing={4}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <FormControl>
                <FormLabel>Cidade</FormLabel>
                <Input value={cityForm.cidade} onChange={(e) => setCityForm({ ...cityForm, cidade: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Estado</FormLabel>
                <Input value={cityForm.estado} onChange={(e) => setCityForm({ ...cityForm, estado: e.target.value })} />
              </FormControl>
            </SimpleGrid>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <FormControl>
                <FormLabel>Modo visual</FormLabel>
                <Select value={cityForm.visual_mode} onChange={(e) => setCityForm({ ...cityForm, visual_mode: e.target.value })}>
                  <option value="google">Google Maps</option>
                  <option value="image">Imagem com rede</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Ativa</FormLabel>
                <Select value={cityForm.ativa ? "1" : "0"} onChange={(e) => setCityForm({ ...cityForm, ativa: e.target.value === "1" })}>
                  <option value="1">Sim</option>
                  <option value="0">Não</option>
                </Select>
              </FormControl>
            </SimpleGrid>
            <FormControl>
              <FormLabel>Título</FormLabel>
              <Input value={cityForm.titulo} onChange={(e) => setCityForm({ ...cityForm, titulo: e.target.value })} />
            </FormControl>
            <FormControl>
              <FormLabel>Subtítulo</FormLabel>
              <Input value={cityForm.subtitulo} onChange={(e) => setCityForm({ ...cityForm, subtitulo: e.target.value })} />
            </FormControl>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              <FormControl>
                <FormLabel>Lat. centro</FormLabel>
                <Input value={cityForm.map_center_lat} onChange={(e) => setCityForm({ ...cityForm, map_center_lat: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Lng. centro</FormLabel>
                <Input value={cityForm.map_center_lng} onChange={(e) => setCityForm({ ...cityForm, map_center_lng: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Zoom</FormLabel>
                <Input value={cityForm.map_zoom} onChange={(e) => setCityForm({ ...cityForm, map_zoom: e.target.value })} />
              </FormControl>
            </SimpleGrid>
            <FormControl>
              <FormLabel>Imagem base da rede</FormLabel>
              <Input type="file" accept="image/*" onChange={(e) => setCityBgFile(e.target.files?.[0] || null)} />
            </FormControl>
            <FormControl>
              <FormLabel>Imagem da home da cidade</FormLabel>
              <Input type="file" accept="image/*" onChange={(e) => setCityHomeFile(e.target.files?.[0] || null)} />
            </FormControl>
            <HStack>
              <Button colorScheme="teal" onClick={handleSaveCity} isLoading={saving}>
                {cityForm.id ? "Atualizar cidade" : "Adicionar cidade"}
              </Button>
              <Button variant="ghost" onClick={() => setCityForm(emptyCityForm)}>
                Limpar
              </Button>
            </HStack>
          </Stack>

          <Stack spacing={3}>
            {cityOptions.length ? (
              cityOptions.map((city) => (
                <Box key={city.id} border="1px solid" borderColor="gray.100" borderRadius="xl" p={4}>
                  <HStack justify="space-between" align="flex-start">
                    <Stack spacing={1}>
                      <Text fontWeight="bold">
                        {city.cidade}{city.estado ? ` - ${city.estado}` : ""}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        Modo {city.visual_mode === "image" ? "Imagem com rede" : "Google Maps"}
                      </Text>
                    </Stack>
                    <HStack>
                      <Button size="xs" onClick={() => startEditCity(city)}>
                        Editar
                      </Button>
                      <Button size="xs" colorScheme="red" onClick={() => handleDeleteCity(city.id)}>
                        Excluir
                      </Button>
                    </HStack>
                  </HStack>
                  {city.background_image_url ? (
                    <Image
                      mt={3}
                      src={assetUrl(city.background_image_url)}
                      borderRadius="lg"
                      maxH="140px"
                      objectFit="cover"
                      width="100%"
                    />
                  ) : null}
                </Box>
              ))
            ) : (
              <Text fontSize="sm" color="gray.500">
                Nenhuma cidade configurada ainda.
              </Text>
            )}
          </Stack>
        </SimpleGrid>
      </Box>
    </Stack>
  );
};

export default EmpresaBrandingConfig;
